import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const MemberCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
`;

const MemberImage = styled.div`
  height: 250px;
  overflow: hidden;
  background-color: #f0f0f0;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s;
  }
  
  ${MemberCard}:hover & img {
    transform: scale(1.05);
  }
`;

const MemberInfo = styled.div`
  padding: 1.5rem;
`;

const MemberName = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const MemberRole = styled.div`
  font-size: 1rem;
  color: ${props => props.accentColor || '#0066cc'};
  margin-bottom: 1rem;
  font-weight: 500;
`;

const MemberBio = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  color: #666;
  transition: color 0.3s;
  
  &:hover {
    color: ${props => props.hoverColor || '#0066cc'};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tag = styled.span`
  background: ${props => props.background || '#f0f4f8'};
  color: ${props => props.color || '#0066cc'};
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 500;
`;

// Social media icons
const getSocialIcon = (name) => {
  // Handle undefined or null name
  if (!name) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="16"></line>
        <line x1="8" y1="12" x2="16" y2="12"></line>
      </svg>
    );
  }
    switch (name.toLowerCase()) {
      case 'twitter':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
          </svg>
        );
      case 'github':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
          </svg>
        );
      case 'linkedin':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
            <rect x="2" y="9" width="4" height="12"></rect>
            <circle cx="4" cy="4" r="2"></circle>
          </svg>
        );
      case 'email':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
        );
      case 'website':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
        );
    }
};

const TeamMember = ({ 
  name,
  role,
  bio,
  image,
  tags = [],
  socials = [],
  accentColor = '#0066cc',
  tagBackground = '#f0f4f8',
  tagColor = '#0066cc',
  animationDelay = 0
}) => {
  // Safe default values for props
  const safeProps = {
    name: name || 'Team Member',
    role: role || 'Contributor',
    bio: bio || '',
    image: image || '/assets/images/placeholder-profile.jpg',
    // Map socials to ensure consistent key names and filter out invalid ones
    socials: socials
      .filter(social => social && social.url)
      .map(social => ({
        ...social,
        // Use name if available, otherwise use platform, or default to 'link'
        name: social.name || social.platform || 'link'
      }))
  };
  
  return (
    <MemberCard
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: animationDelay }}
    >
      <MemberImage>
        <img src={safeProps.image} alt={safeProps.name} />
      </MemberImage>
      <MemberInfo>
        <MemberName>{safeProps.name}</MemberName>
        <MemberRole accentColor={accentColor}>{safeProps.role}</MemberRole>
        
        {tags.length > 0 && (
          <TagList>
            {tags.map((tag, index) => (
              <Tag 
                key={index}
                background={tagBackground}
                color={tagColor}
              >
                {tag}
              </Tag>
            ))}
          </TagList>
        )}
        
        <MemberBio>{safeProps.bio}</MemberBio>
        
        {safeProps.socials.length > 0 && (
          <SocialLinks>
            {safeProps.socials.map((social, index) => (
              <SocialLink 
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                hoverColor={accentColor}
                aria-label={social.name}
              >
                {getSocialIcon(social.name || social.platform)}
              </SocialLink>
            ))}
          </SocialLinks>
        )}
      </MemberInfo>
    </MemberCard>
  );
};

// Attach as static method
TeamMember.getSocialIcon = getSocialIcon;

export default TeamMember;
